import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Switch, Spinner, Typography, ConfirmationModal, useOverlayContext } from 'seagull';
import { useStoreActions } from '~/contexts';
import { timeModel } from '~/models';
import { useBillingObservable } from '~/providers/BillingObservableProvider';
import styles from '../BillingControl.module.scss';
function PauseBillingConfirmationModal(props) {
    const { serviceName, dismissModal } = props;
    const title = 'Pause automatic billing';
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: title,
        testId: "pause-confirmation-modal",
        textAlignCenter: false,
        width: "701",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        "You are about to pause automatic billing for",
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "pause-confirmation-modal-service-name",
                            as: "span",
                            weight: "bold",
                            children: serviceName
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    children: "The service will not be billed until you manually resume automatic billing for it."
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    style: {
                        marginTop: '1rem'
                    },
                    children: "Be sure to resume automatic billing several days before the billing date in order to avoid missing the next billing cycle."
                })
            ]
        }),
        primaryButtonLabel: title,
        secondaryButtonLabel: "Cancel",
        onPrimaryButtonClick: ()=>dismissModal(true),
        onSecondaryButtonClick: ()=>dismissModal(false)
    });
}
function ResumeBillingConfirmationModal(props) {
    const { serviceName, nextBillingDate, dismissModal } = props;
    const title = 'Resume automatic billing';
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: title,
        testId: "resume-confirmation-modal",
        textAlignCenter: false,
        width: "701",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        "You are about to activate automatic billing for",
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "resume-confirmation-modal-service-name",
                            as: "span",
                            weight: "bold",
                            children: serviceName
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    style: {
                        marginTop: '1rem'
                    },
                    children: [
                        "The upcoming invoice to include this service will be on",
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "resume-confirmation-modal-next-billing-date",
                            as: "span",
                            weight: "bold",
                            children: timeModel.formatDate(nextBillingDate)
                        }),
                        "."
                    ]
                })
            ]
        }),
        primaryButtonLabel: title,
        secondaryButtonLabel: "Cancel",
        onPrimaryButtonClick: ()=>dismissModal(true),
        onSecondaryButtonClick: ()=>dismissModal(false)
    });
}
export default function OngoingServiceSwitch(props) {
    const { agreementId, serviceId, serviceName, isPaused, isDisabled, tooltipTitle, testId } = props;
    const { notifyToRefresh } = useBillingObservable();
    const [isResumed, setIsResumed] = useState(!isPaused);
    const [loading, setLoading] = useState(false);
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    async function onChange(e) {
        const { checked, value } = e.target;
        if (!checked /* pause service */ ) {
            const confirmed = await overlayContext.showModal({
                content: (props)=>/*#__PURE__*/ _jsx(PauseBillingConfirmationModal, _object_spread_props(_object_spread({}, props), {
                        serviceName: serviceName
                    }))
            });
            if (!confirmed) return;
        } else {
            const nextBillingDate = await api.service.nextBillingDate(agreementId, serviceId);
            const confirmed = await overlayContext.showModal({
                content: (props)=>/*#__PURE__*/ _jsx(ResumeBillingConfirmationModal, _object_spread_props(_object_spread({}, props), {
                        serviceName: serviceName,
                        nextBillingDate: nextBillingDate
                    }))
            });
            if (!confirmed) return;
        }
        try {
            setLoading(true);
            await api.service.togglePauseResumeBilling(agreementId, value, !checked);
            notifyToRefresh();
            setIsResumed(checked);
        } catch (error) {
            overlayContext.showErrorToast("Failed to ".concat(checked ? 'resume' : 'pause', " service"), 'pause-service-error-toast');
        } finally{
            setLoading(false);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.switch,
        children: [
            loading && /*#__PURE__*/ _jsx(Spinner, {
                size: 24
            }),
            /*#__PURE__*/ _jsx(Switch, {
                disabled: loading || isDisabled,
                value: serviceId,
                checked: isResumed,
                testId: testId,
                tooltip: tooltipTitle ? {
                    placement: 'top',
                    title: /*#__PURE__*/ _jsx("div", {
                        "data-testid": testId && "".concat(testId, "-tooltip"),
                        children: tooltipTitle
                    })
                } : undefined,
                onChange: onChange
            })
        ]
    });
}
